import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { PatrimonioCentroCustoModel } from "../../domain/models/patrimonio_centro_custo";
import { initialPatrimonioCentroCustoLista, usePatrimonioCentroCustoListaFilter } from "../hooks/PatrimonioMaquinaEquipamento/filters";
import { usePatrimonioCentroCustoListaPagination } from "../hooks/PatrimonioMaquinaEquipamento/pagination";
import { PatrimonioCentroCustoForm } from "../schemas/PatrimonioMaquinaEquip/patrimonio_centro_custo";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";

/**
 * LoadPatrimonioCentroCustoFuncState
 */

type LoadPatrimonioCentroCustoFuncState = {
  status?: number;
  idsCentroCusto?: number[];

  page?: number;
  rowsPerPage?: number;
};

/**
 * usePatrimonioCentroCustoListaViewModel
 */

export const usePatrimonioCentroCustoListaViewModel = () => {
  const {
    controlePatrimonio: {
      getAllPatrimonioCentroCustoUseCase,
      getPatrimonioCentroCustoByIdUseCase,
      createPatrimonioCentroCustoUseCase,
      updatePatrimonioCentroCustoUseCase,
      deletePatrimonioCentroCustoUseCase,
    },
    commons: {
      getCentroCustoDropDownUseCase
    }
  } = useDependencyInjection();

  const { pushErrorNotification, pushInfoNotification, setPageSaving, setCustomLoading } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();
  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [patrimonioCentroCustos, setPatrimonioCentroCustos] = useState<PatrimonioCentroCustoModel[]>();

  const [selectedPatrimonioCentroCusto, setSelectedPatrimonioCentroCusto] = useState<PatrimonioCentroCustoModel>();

  const [patrimonioCentroCustoModal, setPatrimonioCentroCustoModal] = useState(false);
  const [patrimonioCentroCustoDeleteModal, setPatrimonioCentroCustoDeleteModal] = useState(false);
  const [copyPatrimonioCentroCustoModal, setCopyPatrimonioCentroCustoModal] = useState(false);

  const filters = usePatrimonioCentroCustoListaFilter();
  const pagination = usePatrimonioCentroCustoListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  const [ddlCentroCusto, setDdlCentroCusto] = useState<CentroCustoDropDown[]>();

  /**
   * Funcoes de carregamento
   */

  const loadPatrimonioCentroCustos = useCallback(async (fs?: LoadPatrimonioCentroCustoFuncState) => {
    try {
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);
      const filteredIdsCentroCusto = evaluateParamValue(fs?.idsCentroCusto, filters.idsCentroCusto);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllPatrimonioCentroCustoUseCase.getAllPatrimonioCentroCusto(
        filteredStatus === 2 ? null : filteredStatus,
        filteredIdsCentroCusto,

        filteredPage,
        filteredRowsPerPage
      );

      setPatrimonioCentroCustos(getAllPatrimonioCentroCustoUseCase.items);
      pagination.setTotalItems(getAllPatrimonioCentroCustoUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error('Falha ao carregar dados da pagina!');
    }
  }, [
    getAllPatrimonioCentroCustoUseCase,
    filters,
    pagination,
  ]);

  const loadPatrimonioCentroCusto = useCallback(async (idPatrimonioCentroCusto: number) => {
    try {
      setLoading(true);
      const patrimonioCentroCusto = await getPatrimonioCentroCustoByIdUseCase.getPatrimonioCentroCustoById(idPatrimonioCentroCusto);
      setSelectedPatrimonioCentroCusto({
        ...patrimonioCentroCusto,
        idPatrimonioCentroCusto: patrimonioCentroCusto.idPatrimonioCentroCusto ?? idPatrimonioCentroCusto,
      });
    } catch (e: unknown) {
      throw new Error('Falha ao carregar dados do registro!');
    } finally {
      setLoading(false);
    }
  }, [
    getPatrimonioCentroCustoByIdUseCase,
  ]);

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response);
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar Centro de Custo!'));
    } finally {
      setLoading(false);
    }
  }, [
    getCentroCustoDropDownUseCase,
    setLoading,
    pushErrorNotification
  ]);


  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadPatrimonioCentroCustos();
      await loadCentroCustoDropDown();
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadPatrimonioCentroCustos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPatrimonioCentroCustos,
    pushErrorNotification,
  ]);

  const handlePatrimonioCentroCustoSubmit = useCallback(async (form: PatrimonioCentroCustoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedPatrimonioCentroCusto) {
        await updatePatrimonioCentroCustoUseCase.updatePatrimonioCentroCusto(
          selectedPatrimonioCentroCusto.idPatrimonioCentroCusto ?? 0,
          form.idCentroCusto,
          form.status
        );
      } else {
        await createPatrimonioCentroCustoUseCase.createPatrimonioCentroCusto(
          form.idCentroCusto,
          form.status
        );
      }
      await loadPatrimonioCentroCustos();

      setPatrimonioCentroCustoModal(false);
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar checklist de serviço!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
      setSelectedPatrimonioCentroCusto(undefined);
    }
  }, [
    selectedPatrimonioCentroCusto,
    createPatrimonioCentroCustoUseCase,
    updatePatrimonioCentroCustoUseCase,
    loadPatrimonioCentroCustos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePatrimonioCentroCustoDeleteSubmit = useCallback(async () => {
    if (selectedPatrimonioCentroCusto) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deletePatrimonioCentroCustoUseCase.deletePatrimonioCentroCusto(
          selectedPatrimonioCentroCusto.idPatrimonioCentroCusto ?? 0,
        );
        await loadPatrimonioCentroCustos();
        setPatrimonioCentroCustoDeleteModal(false);
        setSelectedPatrimonioCentroCusto(undefined);
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao excluir checklist de serviço!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    selectedPatrimonioCentroCusto,
    deletePatrimonioCentroCustoUseCase,
    loadPatrimonioCentroCustos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  // const handleCopyChecklistServicoSubmit = useCallback(async (form: CopyChecklistServicoForm) => {
  //   if (selectedChecklistServico) {
  //     try {
  //       setSaving(true);
  //       setPageSaving(true);

  //       await copyChecklistServicoUseCase.copyChecklistServico(
  //         selectedChecklistServico.idChecklistServico,
  //         form.codigo,
  //         form.nroRevisao,
  //         form.descricao,
  //       );

  //       await loadChecklistServicos();

  //       setCopyChecklistServicoModal(false);
  //       setSelectedChecklistServico(undefined);

  //       pushInfoNotification('Sucesso', 'Registro copiado com sucesso!');
  //     } catch (e: unknown) {
  //       pushErrorNotification("Erro", processError(e, "Falha ao copiar checklist de serviço!"));
  //     } finally {
  //       setSaving(false);
  //       setPageSaving(false);
  //     }
  //   }
  // }, [
  //   selectedChecklistServico,
  //   copyChecklistServicoUseCase,
  //   loadChecklistServicos,
  //   pushErrorNotification,
  //   pushInfoNotification,
  //   setPageSaving,
  // ]);

  const handleStatusChange = useDebouncedCallback(debouncedHandler, (status?: number) => {
    usePatrimonioCentroCustoListaFilter.setState({ status });
    return async () => {
      try {
        setLoading(true);
        usePatrimonioCentroCustoListaPagination.setState({ page: 1 });
        await loadPatrimonioCentroCustos({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    pushErrorNotification,
    loadPatrimonioCentroCustos,
  ]);

  const handleIdsCentroCustoChange = useDebouncedCallback(
    debouncedHandler,
    (newValues: number[]) => {
      usePatrimonioCentroCustoListaFilter.setState({ idsCentroCusto: newValues });
      return async () => {
        try {
          setLoading(true);
          usePatrimonioCentroCustoListaPagination.setState({ page: 1 });
          await loadPatrimonioCentroCustos({ idsCentroCusto: newValues, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    }, [usePatrimonioCentroCustoListaFilter,
    usePatrimonioCentroCustoListaPagination,
    loadPatrimonioCentroCustos]
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      usePatrimonioCentroCustoListaFilter.setState({ ...initialPatrimonioCentroCustoLista })
      usePatrimonioCentroCustoListaPagination.setState({ page: 1 });
      await loadPatrimonioCentroCustos({ ...initialPatrimonioCentroCustoLista, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPatrimonioCentroCustos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      usePatrimonioCentroCustoListaPagination.setState({ page: page + 1 });
      await loadPatrimonioCentroCustos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPatrimonioCentroCustos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      usePatrimonioCentroCustoListaPagination.setState({ page: 1, rowsPerPage });
      await loadPatrimonioCentroCustos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPatrimonioCentroCustos,
    pushErrorNotification,
  ]);

  const handlePatrimonioCentroCustoModal = useCallback(async (idPatrimonioCentroCusto: number | null, openState: boolean) => {
    if (openState) {
      if (idPatrimonioCentroCusto) {
        await loadPatrimonioCentroCusto(idPatrimonioCentroCusto);
      } else {
        setSelectedPatrimonioCentroCusto(undefined);
      }
    }
    setPatrimonioCentroCustoModal(openState);
  }, [
    loadPatrimonioCentroCusto,
  ]);

  const handlePatrimonioCentroCustoDeleteModal = useCallback((idPatrimonioCentroCusto: number | null, openState: boolean) => {
    if (openState) {
      if (idPatrimonioCentroCusto && patrimonioCentroCustos) {
        setSelectedPatrimonioCentroCusto(patrimonioCentroCustos.find(x => x.idPatrimonioCentroCusto === idPatrimonioCentroCusto));
      } else {
        setSelectedPatrimonioCentroCusto(undefined);
      }
    }
    setPatrimonioCentroCustoDeleteModal(openState);
  }, [
    patrimonioCentroCustos,
  ]);

  // const handleCopyChecklistServicoModal = useCallback((idChecklistServico: number | null, openState: boolean) => {
  //   if (openState) {
  //     if (idChecklistServico && checklistServicos) {
  //       setSelectedChecklistServico(checklistServicos.find(x => x.idChecklistServico === idChecklistServico));
  //     } else {
  //       setSelectedChecklistServico(undefined);
  //     }
  //   }
  //   setCopyChecklistServicoModal(openState);
  // }, [
  //   checklistServicos,
  // ]);

  const handleEnterChecklist = useCallback((idPatrimonioCentroCusto: number | null, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/Patrimonio_cadastroInspecao?id=${idPatrimonioCentroCusto}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/Patrimonio_cadastroInspecao?id=${idPatrimonioCentroCusto}`, { alternativeRoute: `/Patrimonio_maquinaEquip` });
    }
  }, [
    backOrNavigate,
  ]);

  // const handleEnterVisualize = useCallback((idChecklistServico: number | null, openNewTab: boolean) => {
  //   if (openNewTab) {
  //     window.open(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, '_blank', 'noopener,noreferrer');
  //   } else {
  //     backOrNavigate(`/ChecklistServico_obrasItem?id=${idChecklistServico}`, { alternativeRoute: `/ChecklistServico_lista` });
  //   }
  // }, [
  //   backOrNavigate,
  // ]);


  return {
    loading,
    saving,
    errorMessage,
    patrimonioCentroCustos: patrimonioCentroCustos ?? [],
    selectedPatrimonioCentroCusto,
    patrimonioCentroCustoModal,
    patrimonioCentroCustoDeleteModal,
    copyPatrimonioCentroCustoModal,
    filters,
    pagination,
    reportViewerState,
    ddlCentroCusto: ddlCentroCusto ?? [],

    handleIdsCentroCustoChange,
    handleReload,
    handlePatrimonioCentroCustoSubmit,
    handlePatrimonioCentroCustoDeleteSubmit,
    // handleCopyChecklistServicoSubmit,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handlePatrimonioCentroCustoModal,
    handlePatrimonioCentroCustoDeleteModal,
    handleEnterChecklist,
  };
}